import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import "./createServiceModal.css";
import { FiClock, FiTag, FiTool } from "react-icons/fi";
import { IoChevronDownOutline, IoCheckmarkSharp } from "react-icons/io5";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";

export default function CreateServiceModal({ onClose, onServiceCreated }) {
  const { t } = useTranslation();

  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [description, setDescription] = useState("");
  const [duration, setDuration] = useState(15);
  const [customDuration, setCustomDuration] = useState("");
  const [numServices, setNumServices] = useState("Unlimited");
  const [customNum, setCustomNum] = useState("");

  const [openDropdown, setOpenDropdown] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const modalRef = useRef(null);

  const durationOptions = [
    { label: t("serviceModal.optMinutes", { count: 15 }), value: 15 },
    { label: t("serviceModal.optMinutes", { count: 30 }), value: 30 },
    { label: t("serviceModal.optHour"), value: 60 },
    { label: t("serviceModal.optHours", { count: 2 }), value: 120 },
    { label: t("serviceModal.optOther"), value: "Other" },
  ];

  const numOptions = [
    { label: "10", value: "10" },
    { label: "20", value: "20" },
    { label: t("serviceModal.optUnlimited"), value: "Unlimited" },
    { label: t("serviceModal.optOther"), value: "Other" }
  ];

  const toggleDropdown = (type) => {
    if (loading) return;
    setOpenDropdown((prev) => (prev === type ? null : type));
  };

  const handleSelect = (type, value) => {
    if (loading) return;

    if (type === "duration") {
      setDuration(value);
      if (value !== "Other") setCustomDuration("");
    }

    if (type === "services") {
      setNumServices(value);
      if (value !== "Other") setCustomNum("");
    }

    setOpenDropdown(null);
  };

  const validateNumberInput = (value, fieldName) => {
    const numValue = parseFloat(value);

    if (fieldName === "price") {
      if (Number.isNaN(numValue) || numValue <= 0) {
        return t("serviceModal.errorPricePositive");
      }
    }

    if (fieldName === "customDuration") {
      if (Number.isNaN(numValue) || numValue <= 0) {
        return t("serviceModal.errorDurationPositive");
      }
      if (numValue > 1440) {
        return t("serviceModal.errorDurationMax");
      }
    }

    if (fieldName === "customNum") {
      if (Number.isNaN(numValue) || numValue <= 0) {
        return t("serviceModal.errorNumPositive");
      }
      if (numValue > 9999) {
        return t("serviceModal.errorNumMax");
      }
    }

    return "";
  };

  const validateForm = () => {
    const newErrors = {};

    if (!name.trim()) {
      newErrors.name = t("serviceModal.errorNameRequired");
    }

    const priceError = validateNumberInput(price, "price");
    if (priceError) {
      newErrors.price = priceError;
    }

    if (!description.trim()) {
      newErrors.description = t("serviceModal.errorDescriptionRequired");
    }

    if (duration === "Other") {
      if (!customDuration) {
        newErrors.customDuration = t("serviceModal.errorCustomDurationRequired");
      } else {
        const durationError = validateNumberInput(
          customDuration,
          "customDuration"
        );
        if (durationError) {
          newErrors.customDuration = durationError;
        }
      }
    }

    if (numServices === "Other") {
      if (!customNum) {
        newErrors.customNum = t("serviceModal.errorCustomNumRequired");
      } else {
        const numError = validateNumberInput(customNum, "customNum");
        if (numError) {
          newErrors.customNum = numError;
        }
      }
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      toast.error(t("serviceModal.toastFixErrors"));
      return false;
    }

    return true;
  };

  const handlePriceChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setPrice(value);
    }
  };

  const handleCustomDurationChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) {
      setCustomDuration(value);
    }
  };

  const handleCustomNumChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) {
      setCustomNum(value);
    }
  };

  const handleCreate = async () => {
    if (loading) return;
    if (!validateForm()) return;

    const finalDuration =
      duration === "Other"
        ? parseInt(customDuration, 10) || 30
        : parseInt(duration, 10);

    const finalServiceCount =
      numServices === "Other"
        ? parseInt(customNum, 10) || 1
        : numServices === "Unlimited" || !numServices
          ? "Unlimited"
          : parseInt(numServices, 10);

    setLoading(true);

    try {
      const newService = {
        name: name.trim(),
        description: description.trim(),
        duration: finalDuration,
        price: parseFloat(price),
        service_count: finalServiceCount,
        currency: "USD",
        time_unit: "minutes",
        on_off: false,
        id: Date.now(),
      };

      if (onServiceCreated) {
        await Promise.resolve(onServiceCreated(newService));
      }

      toast.success(t("serviceModal.toastSuccess"));
      onClose();
    } catch (error) {
      console.error("Error creating service:", error);
      toast.error(error?.message || t("serviceModal.errorGenericCreate"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (loading) return;
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [loading]);

  return (
    <AnimatePresence>
      <motion.div
        className="create-service-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        onClick={(e) => {
          if (loading) return;
          if (e.target.classList.contains("create-service-overlay")) onClose();
        }}
      >
        <motion.div
          ref={modalRef}
          className="create-service-modal"
          initial={{ opacity: 0, scale: 0.8, y: 40 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.85, y: 30 }}
          transition={{ type: "spring", stiffness: 150, damping: 18 }}
        >
          <div className="create-service-header">
            <button
              className="create-service-back-btn"
              onClick={onClose}
              disabled={loading}
            >
              {t("serviceModal.btnBack")}
            </button>
            <div className="create-service-title-section">
              <h2>{t("serviceModal.modalTitle")}</h2>
              <p>{t("serviceModal.modalSubtitle")}</p>
            </div>
          </div>

          <div className="create-service-form">
            <div className="create-service-input-row">
              <div className="create-service-input-group">
                <label>{t("serviceModal.labelName")}</label>
                <input
                  type="text"
                  placeholder={t("serviceModal.placeholderName")}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={errors.name ? "invalid" : ""}
                  disabled={loading}
                />
                {errors.name && (
                  <span className="input-error-text">{errors.name}</span>
                )}
              </div>

              <div className="create-service-input-group create-service-dropdown">
                <label>{t("serviceModal.labelDuration")}</label>
                <div
                  className={`create-service-dropdown-field ${
                    errors.customDuration ? "invalid" : ""
                  } ${loading ? "disabled" : ""}`}
                  onClick={() => toggleDropdown("duration")}
                >
                  <FiClock className="create-service-icon" />
                  <span className="create-service-dropdown-text">
                    {duration === "Other"
                      ? `${customDuration || t("serviceModal.optCustom")} ${t("serviceModal.unitMin")}`
                      : `${duration} ${t("serviceModal.unitMin")}`}
                  </span>
                  <IoChevronDownOutline className="create-service-chevron" />
                </div>

                <AnimatePresence>
                  {openDropdown === "duration" && !loading && (
                    <motion.div
                      className="create-service-dropdown-menu"
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.2 }}
                    >
                      {durationOptions.map((opt) => (
                        <div
                          key={opt.value}
                          className="create-service-dropdown-item"
                          onClick={() => handleSelect("duration", opt.value)}
                        >
                          <span>{opt.label}</span>
                          {duration === opt.value ? (
                            <div className="create-service-success-check">
                              <IoCheckmarkSharp />
                            </div>
                          ) : (
                            <div className="create-service-empty-circle"></div>
                          )}
                        </div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>

                {duration === "Other" && (
                  <>
                    <input
                      type="text"
                      className={`create-service-custom-input ${
                        errors.customDuration ? "invalid" : ""
                      }`}
                      placeholder={t("serviceModal.placeholderCustomDuration")}
                      value={customDuration}
                      onChange={handleCustomDurationChange}
                      disabled={loading}
                    />
                    {errors.customDuration && (
                      <span className="input-error-text">
                        {errors.customDuration}
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="create-service-input-row">
              <div className="create-service-input-group">
                <label>{t("serviceModal.labelPrice")}</label>
                <div
                  className={`create-service-input-icon-field ${
                    errors.price ? "invalid" : ""
                  }`}
                >
                  <FiTag className="create-service-icon" />
                  <input
                    type="text"
                    placeholder={t("serviceModal.placeholderPrice")}
                    value={price}
                    onChange={handlePriceChange}
                    className="create-service-number-input"
                    disabled={loading}
                  />
                </div>
                {errors.price && (
                  <span className="input-error-text">{errors.price}</span>
                )}
              </div>

              <div className="create-service-input-group create-service-dropdown">
                <label>{t("serviceModal.labelNumServices")}</label>
                <div
                  className={`create-service-dropdown-field ${
                    errors.customNum ? "invalid" : ""
                  } ${loading ? "disabled" : ""}`}
                  onClick={() => toggleDropdown("services")}
                >
                  <FiTool className="create-service-icon" />
                  <span className="create-service-dropdown-text">
                    {numServices === "Other"
                      ? customNum || t("serviceModal.optCustom")
                      : numServices === "Unlimited"
                        ? t("serviceModal.optUnlimited")
                        : numServices}
                  </span>
                  <IoChevronDownOutline className="create-service-chevron" />
                </div>

                <AnimatePresence>
                  {openDropdown === "services" && !loading && (
                    <motion.div
                      className="create-service-dropdown-menu"
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.2 }}
                    >
                      {numOptions.map((opt) => (
                        <div
                          key={opt.value}
                          className="create-service-dropdown-item"
                          onClick={() => handleSelect("services", opt.value)}
                        >
                          <span>{opt.label}</span>
                          {numServices === opt.value ? (
                            <div className="create-service-success-check">
                              <IoCheckmarkSharp />
                            </div>
                          ) : (
                            <div className="create-service-empty-circle"></div>
                          )}
                        </div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>

                {numServices === "Other" && (
                  <>
                    <input
                      type="text"
                      className={`create-service-custom-input ${
                        errors.customNum ? "invalid" : ""
                      }`}
                      placeholder={t("serviceModal.placeholderCustomNum")}
                      value={customNum}
                      onChange={handleCustomNumChange}
                      disabled={loading}
                    />
                    {errors.customNum && (
                      <span className="input-error-text">
                        {errors.customNum}
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="create-service-input-group create-service-textarea">
              <label>{t("serviceModal.labelDescription")}</label>
              <textarea
                placeholder={t("serviceModal.placeholderDescription")}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className={errors.description ? "invalid" : ""}
                disabled={loading}
              />
              {errors.description && (
                <span className="input-error-text">{errors.description}</span>
              )}
            </div>
          </div>

          <div className="create-service-footer">
            <button
              className="create-service-btn"
              onClick={handleCreate}
              disabled={loading}
            >
              {loading ? t("serviceModal.btnCreating") : t("serviceModal.btnCreate")}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}